import { useEffect, useRef } from "react";
import { useUserpilot } from "@platform/hooks/useUserpilot";

import useUserStore from "@store/useUserStore";
import useDialogStore from "@store/useDialogStore";
import useSystemStore from "@store/useSystemStore";
import useMeetingStore from "@store/useMeetingStore";
import usePlatformStore from "@store/usePlatformStore";
import { useGetMeeting } from "@screens/meeting/hooks/useGetMeeting";
import { useGetUser } from "@screens/connect-meridian/hooks/useGetUser";
import { useSyncMeeting } from "@screens/meeting/hooks/useSyncMeeting";
import { ActiveScreen } from "@enums/screens.enum";
import { ItemType } from "@enums/common.enum";

const UI_TRANSITION_DELAY_SHORT_MS = 800;

export function useMeetingInitialization(hasSession: boolean) {
  const { track } = useUserpilot();
  const { isUserConnected, isPending } = useUserStore();
  const { itemType, itemId, calendarId } = usePlatformStore();
  const lastItemId = useRef<string | undefined>(undefined);
  const { isAppLoading, setIsAppLoading, changeActiveScreen } = useSystemStore();
  const { closeDialog } = useDialogStore();
  const { setMeetingId } = useMeetingStore();
  const { data: user, error: userError } = useGetUser(false);
  const { mutate: syncMeetingMutate, error: syncMeetingError } = useSyncMeeting();

  const isAutoSynced = useRef(false);
  const isManualSyncRequired = user?.organization?.manual_sync_required ?? false;

  const {
    data: meetingData,
    error: getMeetingError,
    isLoading,
  } = useGetMeeting(isManualSyncRequired);

  const meeting = meetingData?.meeting;
  const isSyncedRecurringSeries = meetingData?.is_synced && meetingData?.is_recurring;
  const hasMeetingError = (!meeting && !isSyncedRecurringSeries) || !!getMeetingError || !!syncMeetingError;

  useEffect(() => {
    // Preload the initial screen when the add-in is loaded and user is connected
    if (!isUserConnected || itemType !== ItemType.MEETING) return;

    if (isLoading) {
      changeActiveScreen(ActiveScreen.SYNC_MEETING);
      return;
    }

    changeActiveScreen(hasMeetingError ? ActiveScreen.SYNC_MEETING : ActiveScreen.MEETING);
  }, [isUserConnected, hasMeetingError, isLoading, isManualSyncRequired]);

  useEffect(() => {
    if (meeting?.id) {
      // Change to the meeting screen after a successful connection (and fetching the meeting)
      changeActiveScreen(ActiveScreen.MEETING);
    }
  }, [meeting]);

  useEffect(() => {
    if (!isManualSyncRequired && isUserConnected && !isAutoSynced.current && itemId) {
      syncMeetingMutate(
        { itemId, calendarId },
        {
          onSuccess: () => {
            isAutoSynced.current = true;
          },
        }
      );
    }
  }, [isManualSyncRequired, isUserConnected, isAutoSynced.current, itemId, calendarId]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (hasSession && !isPending) {
      // Small delay to prevent flickering between screens
      timer = setTimeout(() => setIsAppLoading(false), UI_TRANSITION_DELAY_SHORT_MS);
    }

    if (!!userError || (!isUserConnected && !isPending)) {
      closeDialog();
      changeActiveScreen(ActiveScreen.CONNECT);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isAppLoading, hasSession, userError, isUserConnected, isPending]);

  // Reset store and refs when the user switches to a different item,
  // so the new item gets a fresh sync instead of showing stale data.
  // Triggered by Chrome (URL/selection change) and Outlook (pinned mode ItemChanged).
  useEffect(() => {
    if (!itemId) return;

    if (itemId !== lastItemId.current) {
      lastItemId.current = itemId;
      isAutoSynced.current = false;
      
      // Unlike emails, no platform check needed here — calendar navigation
      // doesn't trigger itemId changes, so no flickering occurs in Chrome.
      setMeetingId(null);
    }
  }, [itemId, setMeetingId]);

  useEffect(() => {
    track("Click: Open Add-in from Calendar");
  }, []);
}
